import { useEffect, useRef, useState } from "react";
import type SimpleBarCore from "simplebar-core";
import {
    ECLeftSideCol,
    ECWrapper,
    EditableItemWrapper,
    EditableSortableItemsWrapper,
    ESItemsWrapper,
    ScrollShortcutPos,
    SimpleBarWithFade,
    UserThemableEditableItem
} from "./EditableSortableItems.styles";
import { EditableItem, EditableSortableType } from "./EditableSortableItems.types";
import { OptionalNextLink } from "../ReusableStyledComponents/OptionalNextLink";
import { CustomDarkShade } from "../ReusableStyledComponents/FontSetUtilities";
import { AuthoryColorSchemes } from "../ProfileHero/V3ProfileHero.types";
import { SORTABLE_HOME_IDENTIFIER } from "../ProfileHero/V4ProfileHero.utils";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useCustomScrollShadows } from "../../hooks/customScrollShadows";

interface ReadOnlySortableItemsProps {
    its: EditableItem[];
    activeCol?: string;
    itemType?: EditableSortableType;
    colorVariant?: AuthoryColorSchemes;
    customBackgroundColor?: string;
    scrollCustomColor?: string;
    useVerticalMode?: boolean;
    onItemSelect: (slug: string) => void;
}

interface ReadOnlyItemProps {
    item: EditableItem;
    active: boolean;
    isCollectionType: boolean;
    colorVariant: AuthoryColorSchemes;
    customBackgroundColor?: string;
    onItemSelect: (slug: string) => void;
}

/**
 * Renders one tab of the visitor view, same markup as a non editing EditableIt.
 * @param props The tab item, its active state and theming
 * @returns The tab element
 */
function ReadOnlyItem({ item, active, isCollectionType, colorVariant, customBackgroundColor, onItemSelect }: ReadOnlyItemProps) {
    /**
     * Selects the tab. A collection tab switches in place on a plain click, its href is for crawlers and for
     * opening the tab in a new window, which a modified click still does.
     * @param e The click event
     */
    function onClick(e: React.MouseEvent) {
        if (isCollectionType && item.href) {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
        }

        onItemSelect(item.id);
    }

    return (
        <div id={item.id} style={{ borderRadius: 6, cursor: "initial" }}>
            <EditableItemWrapper
                $alternateHover={!isCollectionType}
                $activeDrag={false}
                $dragMode={false}
                $isOverlay={false}
                $isEditingName={false}
                $editMode={false}
                $isAddElement={false}
                data-is-add-element="false"
                $colorVariant={colorVariant}
                $dashOnHover={false}
                $customBackgroundColor={customBackgroundColor}
            >
                <OptionalNextLink href={item.href} useNextRouter={true}>
                    <UserThemableEditableItem $colorVariant={colorVariant}>
                        <ECLeftSideCol
                            $customDarkShade={CustomDarkShade.Grey35}
                            $alternateHover={!isCollectionType}
                            $dragMode={false}
                            $editMode={false}
                            $active={active}
                            $isEditingName={false}
                            onClick={onClick}
                            $isAddElement={false}
                            $colorVariant={colorVariant}
                            $hasSettingsHidden={item.hideSettings}
                            $customBackgroundColor={customBackgroundColor}
                        >
                            <div contentEditable={false} dangerouslySetInnerHTML={{ __html: item.label }} />
                        </ECLeftSideCol>
                    </UserThemableEditableItem>
                </OptionalNextLink>
                <div />
            </EditableItemWrapper>
        </div>
    );
}

/**
 * Visitor version of EditableSortableItems. Renders the collection or about page tabs without
 * the drag and drop, inline rename and settings code, which lives in the lazily loaded editor.
 * @param props The tabs, the active tab and theming
 * @returns The scrollable tab bar
 */
export function ReadOnlySortableItems({
    its,
    activeCol,
    itemType = EditableSortableType.Collection,
    colorVariant = AuthoryColorSchemes.userTheme,
    customBackgroundColor,
    scrollCustomColor,
    useVerticalMode = false,
    onItemSelect
}: ReadOnlySortableItemsProps) {
    const [hideScrollShortcut, setHideScrollShortcut] = useState(true);
    const [elem, setElem] = useState<HTMLDivElement>();
    const scrollbarRef = useRef<SimpleBarCore>(undefined);
    const { onScroll, ref, shadowStates } = useCustomScrollShadows();
    const size = useWindowSize();

    const isCollectionType = itemType === EditableSortableType.Collection;
    const hasHomeElement = its[0]?.id === SORTABLE_HOME_IDENTIFIER;
    const homeElement = hasHomeElement ? its[0] : undefined;
    const itemsToRender = hasHomeElement ? its.slice(1) : its;

    const shadowClasses = `${shadowStates.leftShadow ? "left-shadow" : ""} ${shadowStates.rightShadow ? "right-shadow" : ""}`;

    /**
     * Hides the scroll shortcut once the tab bar is scrolled to its end.
     * @param el The scrollable node
     */
    function checkScrollShortcut(el: HTMLDivElement) {
        setHideScrollShortcut(Math.ceil(el.offsetWidth + el.scrollLeft) >= el.scrollWidth);
    }

    useEffect(() => {
        if (elem) checkScrollShortcut(elem);
    }, [size.width, size.height, its]);

    /**
     * Renders a tab with the shared theming props.
     * @param item The tab item
     * @returns The tab element
     */
    function renderItem(item: EditableItem) {
        return (
            <ReadOnlyItem
                key={`${item.id}-${item.label}`}
                item={item}
                active={activeCol === item.id}
                isCollectionType={isCollectionType}
                colorVariant={colorVariant}
                customBackgroundColor={customBackgroundColor}
                onItemSelect={onItemSelect}
            />
        );
    }

    return (
        <ECWrapper>
            <div>
                <SimpleBarWithFade
                    $customBackgroundColor={customBackgroundColor}
                    $scrollCustomColor={scrollCustomColor}
                    $colorVariant={colorVariant}
                    onMouseEnter={() => {
                        if (scrollbarRef.current) scrollbarRef.current.options.autoHide = false;
                    }}
                    onMouseLeave={() => {
                        if (scrollbarRef.current) scrollbarRef.current.options.autoHide = true;
                    }}
                    scrollableNodeProps={{
                        onScroll: (evt: React.UIEvent<HTMLDivElement>) => {
                            checkScrollShortcut(evt.currentTarget);
                            onScroll(evt);
                        },
                        ref: (e: HTMLDivElement) => {
                            setElem(e);
                            ref(e);
                        }
                    }}
                    className={shadowClasses}
                    //@ts-ignore
                    ref={scrollbarRef}
                >
                    <ESItemsWrapper $useVerticalMode={useVerticalMode}>
                        {homeElement && renderItem(homeElement)}
                        <div>
                            <EditableSortableItemsWrapper
                                $editMode={false}
                                $dragMode={false}
                                $editingComponentID=""
                                $useVerticalMode={useVerticalMode}
                                $isDropdownOpen={false}
                                data-intercom-target={isCollectionType ? "ProfileCollectionsBar" : undefined}
                                $colorVariant={colorVariant}
                                $alternateHover={!isCollectionType}
                                $dashOnHover={false}
                                $customBackgroundColor={customBackgroundColor}
                            >
                                {itemsToRender.map(renderItem)}
                            </EditableSortableItemsWrapper>
                        </div>
                    </ESItemsWrapper>
                </SimpleBarWithFade>
            </div>
            {!hideScrollShortcut && isCollectionType && (
                <ScrollShortcutPos $colorVariant={colorVariant}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="13 17 18 12 13 7" />
                        <polyline points="6 17 11 12 6 7" />
                    </svg>
                </ScrollShortcutPos>
            )}
        </ECWrapper>
    );
}
